import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { GetStateResponse, fetchPutState } from '@iwoca/lapi-client/edge';
import { useMutation } from '@tanstack/react-query';
import { cloneDeep, set } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { requestCallbackValidator } from './requestCallbackValidator';
import { useGetStateByStateKey } from '../../../../api/lending/lapiHooks';
import hero from '../../../../components/GiveUsACall/hero.jpg';
import { HookFormInput } from '../../../../components/Input/Input';
import { LoadingSpinner } from '../../../../components/LoadingSpinner/LoadingSpinner';
import Check from '../../../../components/svg/Check.svg';
import PhoneIcon from '../../../../components/svg/PhoneIconWhite.svg';
import {
  iwocapayTelDisplay,
  iwocapayTelLink,
} from '../../../../constants.json';
import { useStateKey } from '../../../../hooks/useStateKey.hook';
import { Button, SubmitButton } from '../../../components/Button/Button';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';

export const SpeakToDirector = () => {
  const [speakToDirectorToggle, setSpeakToDirectorToggle] = useState(false);

  return (
    <div className="rounded-l border border-secondary-80 bg-structure-100 p-xl text-m font-reg text-primary-10">
      <h2 className="mb-xl mt-0 text-center text-2xl font-bold leading-[100%] text-primary-10 max-600:mt-l">
        We need to speak to a director
      </h2>
      <div className="grid grid-cols-[100%]">
        <img
          src={hero}
          className="ml-[50%] h-auto max-w-[250px] -translate-x-[calc(50%+15px)]"
          alt=""
        />
        <p className="!mx-0 !mb-l !mt-l text-left text-m max-600:row-start-1 max-600:!mt-0 max-600:text-center">
          <strong className="font-med">
            You need to be a director of the business to complete this
            application. Give us a quick call{' '}
            <span className="max-600:hidden">
              on{' '}
              <a href={iwocapayTelLink} className="text-primary-40">
                {iwocapayTelDisplay}
              </a>
            </span>{' '}
            and we can explain the next steps.
          </strong>
        </p>
      </div>
      <p className="max-600:hidden">
        Alternatively, you can enter your number below and we’ll give you a call
        as soon as one of the team is available.
      </p>
      <div className="hidden max-600:block">
        <a href={iwocapayTelLink} className="!no-underline">
          <WhiteLabelOverride componentType="button" disabled={false}>
            <SubmitButton className="flex w-full items-center justify-center !no-underline">
              {iwocapayTelLink}
              <img className="ml-s" src={PhoneIcon} alt="Phone Icon" />
            </SubmitButton>
          </WhiteLabelOverride>
        </a>
        {!speakToDirectorToggle && (
          <>
            <p className="!mx-0 !my-l text-center">Or</p>
            <WhiteLabelOverride
              componentType="button"
              disabled={false}
              variant="secondary"
            >
              <Button
                onClick={() => setSpeakToDirectorToggle(!speakToDirectorToggle)}
                className="w-full border border-secondary-80 bg-callback-button text-primary-40 hover:text-white"
              >
                Request callback
              </Button>
            </WhiteLabelOverride>
          </>
        )}
        {speakToDirectorToggle && (
          <>
            <h3 className="mb-l mt-xl">Request a call back</h3>
            <p>
              Enter your contact number below and we'll give you a call as soon
              as one of the team is available.
            </p>
            <RequestCallBackForm />
          </>
        )}
      </div>
      <div className="max-600:hidden">
        <RequestCallBackForm />
      </div>
    </div>
  );
};

type RequestCallBackFormValues = { callbackPhoneNumber: string };

const RequestCallBackForm = () => {
  const [addedNumber, setSuccessAddedNumber] = useState(false);
  const { state, refetchState } = useGetStateByStateKey();
  const { stateKey } = useStateKey();

  const methods = useForm<RequestCallBackFormValues>({
    defaultValues: { callbackPhoneNumber: '' },
    resolver: yupResolver(requestCallbackValidator),
  });

  const { isPending, mutateAsync } = useMutation({
    mutationFn: async (state: GetStateResponse) => {
      if (!stateKey) {
        throw new Error('No statekey provided');
      }

      await fetchPutState({ stateKey, body: state });

      await refetchState();
    },
  });

  const addPhoneNumberToCustomer = async (customerPhoneNumber: string) => {
    const newState = cloneDeep(state);
    if (newState?.application?.people) {
      const modifiedApplicant = newState?.application?.people?.filter(
        (people) => {
          return people.roles?.includes('applicant');
        },
      )[0];

      if (modifiedApplicant) {
        const modifiedApplicantIndex =
          newState.application.people.indexOf(modifiedApplicant);
        const matchingNumbers = modifiedApplicant.phones?.filter(
          (phone) => phone.number === customerPhoneNumber,
        );

        const newPhones: typeof modifiedApplicant.phones = [
          {
            uid: uuidv4(),
            type: 'primary',
            number: customerPhoneNumber,
          },
        ];

        if (!modifiedApplicant.phones) {
          modifiedApplicant.phones = newPhones;

          set(
            newState,
            `application.people[${modifiedApplicantIndex}]`,
            modifiedApplicant,
          );
        } else if (matchingNumbers?.length === 0) {
          modifiedApplicant.phones = modifiedApplicant.phones.concat(newPhones);

          set(
            newState,
            `application.people[${modifiedApplicantIndex}]`,
            modifiedApplicant,
          );
        }

        await mutateAsync({ data: newState });
      }
    }
  };

  const onSubmit = methods.handleSubmit(async ({ callbackPhoneNumber }) => {
    await addPhoneNumberToCustomer(callbackPhoneNumber);
    setSuccessAddedNumber(true);
  });

  return (
    <>
      <p className="text-structure-40">
        Don’t worry – it won’t be shared with anyone else.
      </p>
      <FormProvider {...methods}>
        <form autoComplete="on" onSubmit={onSubmit}>
          <HookFormInput
            label=""
            labelDescriptionText=""
            name="callbackPhoneNumber"
            className="pb-l"
            type="tel"
          />
          <WhiteLabelOverride
            componentType="button"
            disabled={false}
            variant="secondary"
          >
            <SubmitButton
              className="w-full border border-secondary-80 bg-callback-button text-primary-40 hover:text-white"
              disabled={false}
            >
              {!isPending ? 'Request call back' : <LoadingSpinner />}
            </SubmitButton>
          </WhiteLabelOverride>
        </form>
      </FormProvider>
      {addedNumber && (
        <div className="mt-l flex items-center justify-center text-s font-bold text-success-mid">
          <img src={Check} alt="" className="mr-s" />
          Thanks. We’ll be in touch soon.
        </div>
      )}
    </>
  );
};
